import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode } from "vue"

import { useGetInspectionGroupQuery } from '@/entities/inspection-group/hooks';
import { computed, inject, reactive, Ref, ref } from 'vue';
import { required } from '@vuelidate/validators';

import { useCreateAnnotationMutation } from '@/entities/annotation/hooks';
import { useForm } from '@/core/hooks/forms';

import { Annotation } from '@/entities/annotation/types';
import { AnnotationSeverity } from '@/entities/annotation-severity/types';
import { AnnotationType } from '@/entities/annotation-type/types';
import {
  InspectionViewerCreateAnnotationFormInjection,
  inspectionViewerCreateAnnotationFormInjectionKey,
} from '@/teams/visionair/components/InspectionViewer/inspection-viewer.shared';

import {
  NkButton,
  NkForm,
  NkFormItem,
  NkGroup,
  NkOption,
  NkSelect,
  NkTextArea
} from '@/core/components';
import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import AnnotationTypeSelect
  from '@/entities/annotation-type/components/inputs/AnnotationTypeSelect.vue';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { useGetAnnotationTypesQuery } from '@/entities/annotation-type/hooks';
import { orderBy } from 'lodash';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionViewerCreateAnnotationForm',
  setup(__props) {

const { t } = useI18n();

const injected = inject(inspectionViewerCreateAnnotationFormInjectionKey) as InspectionViewerCreateAnnotationFormInjection;
const { props } = injected;

const createAnnotationForm = ref();
// Form
const fields: { severity: AnnotationSeverity['id'] | null, type: AnnotationType['id'] | null, comment: Annotation['comment'] } = reactive({
  severity: null,
  type: null,
  comment: '',
});

const rules = {
  severity: { required },
  type: { required },
};

const inspectionGroupQueryData = computed(() => ({ id: props.inspectionGroup }));
const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value.id);
const { data: inspectionGroupData } = useGetInspectionGroupQuery(inspectionGroupQueryData, { enabled: hasInspectionGroupQueryData });

const annotationTypesQueryFilters: Ref = computed(() => ({ organizations: 0, itemsPerPage: 50 }));
const { data: annotationTypesData } = useGetAnnotationTypesQuery({
  refetchOnWindowFocus: false,
}, annotationTypesQueryFilters);
const annotationTypes = computed(() => orderBy((annotationTypesData.value || []), ['order', 'name']));

const { onSubmitted } = useForm(injected.emit);

const { mutateAsync: createAnnotationMutation } = useCreateAnnotationMutation();

const createAnnotation = () => {
  createAnnotationForm.value.validator.$validate()
    .then((valid: boolean) => {
      if (valid) {
        createAnnotationMutation({
          position: {
            pixelX: Math.round(props.completeLocation.pixelLocation.x),
            pixelY: Math.round(props.completeLocation.pixelLocation.y),
            pixelHeight: Math.round(props.completeLocation.pixelLocation.height),
            pixelWidth: Math.round(props.completeLocation.pixelLocation.width),
            viewportX: props.completeLocation.viewportLocation.x,
            viewportY: props.completeLocation.viewportLocation.y,
            viewportHeight: props.completeLocation.viewportLocation.height,
            viewportWidth: props.completeLocation.viewportLocation.width,
            hubHeight: 0,
            position: 0,
            device: props.device,
          },
          annotationGroups: [],
          annotationImages: [],
          inspection: props.inspection,
          severity: fields.severity as AnnotationSeverity['id'],
          type: fields.type as AnnotationType['id'],
          comment: fields.comment,
          createdBy: null,
          status: 'to fix'
        },
          {
            onSuccess: () => {
              ElNotification({
                title: t('annotation.notification.add.success.title'),
                message: t('annotation.notification.add.success.message'),
                type: 'success',
                position: 'bottom-right',
              });
            }
          }).then(() => {
          onSubmitted();
        }).catch(() => {
          console.log('ANNOTATION CREATE ERROR');
        });
      }
    });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(NkForm), {
    ref_key: "createAnnotationForm",
    ref: createAnnotationForm,
    fields: fields,
    rules: rules
  }, {
    footer: _withCtx(() => [
      _createVNode(_unref(NkGroup), { position: "right" }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkButton), { onClick: createAnnotation }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('annotation.action.add')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.type'),
        prop: "type"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkSelect), {
            modelValue: fields.type,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fields.type) = $event))
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotationTypes), (type) => {
                return (_openBlock(), _createBlock(_unref(NkOption), {
                  key: type.id,
                  value: type.id,
                  label: type.name
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(type.name), 1)
                  ]),
                  _: 2
                }, 1032, ["value", "label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.severity'),
        prop: "severity"
      }, {
        default: _withCtx(() => [
          _createVNode(AnnotationSeveritySelect, {
            service: _unref(inspectionGroupData)?.service,
            modelValue: fields.severity,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.severity) = $event))
          }, null, 8, ["service", "modelValue"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.comment'),
        prop: "comment"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkTextArea), {
            modelValue: fields.comment,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fields.comment) = $event))
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }, 8, ["label"])
    ]),
    _: 1
  }, 8, ["fields"]))
}
}

})